// Geocoding runs in the browser through the Maps JavaScript API rather than the graph.
// The Geocoding web service rejects the API key because it is restricted by referer,
// which the JavaScript API accepts. The returned shape matches the graph's
// geolocation_byaddress/address_bygeolocation calls these replaced.
const addressKeys = ["AddressLine1", "AddressLine2", "AddressLine3", "City", "StateCode", "PostalCode", "CountryCode"];

let mapsPromise;

const loadGoogleMaps = () => {
	if (window.google && window.google.maps) {
		return Promise.resolve(window.google.maps);
	}

	if (!mapsPromise) {
		mapsPromise = fetch("/runtime-config")
			.then(res => res.json())
			.then(({ googleMapsApiKey }) => {
				if (!googleMapsApiKey) {
					throw new Error("GOOGLE_MAPS_API_KEY is not configured");
				}

				return new Promise((resolve, reject) => {
					const script = document.createElement("script");
					script.type = "text/javascript";
					script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
					script.onload = () => resolve(window.google.maps);
					script.onerror = () => reject(new Error("the Google Maps script failed to load"));

					const node = document.getElementsByTagName("script")[0];
					node.parentNode.insertBefore(script, node);
				});
			})
			.catch(err => {
				mapsPromise = undefined;
				throw err;
			});
	}

	return mapsPromise;
};

// success is false only when the lookup itself failed, so callers can tell that apart from
// a lookup that ran and matched nothing and keep the values they already have.
const geocode = async request => {
	let maps;

	try {
		maps = await loadGoogleMaps();
	} catch(err) {
		console.error("geocode failed", err.message);
		return { success: false, results: [] };
	}

	const geocoder = new maps.Geocoder();

	return new Promise(resolve => {
		geocoder.geocode(request, (results, status) => {
			if (status === maps.GeocoderStatus.OK && results && results.length) {
				resolve({ success: true, results });
				return;
			}

			if (status !== maps.GeocoderStatus.ZERO_RESULTS) {
				console.error("geocode failed", status);
				resolve({ success: false, results: [] });
				return;
			}

			resolve({ success: true, results: [] });
		});
	});
};

// Google spreads the components over results of decreasing precision, so the closest match
// can be missing a postal code that a broader result for the same spot still carries.
const addComponents = (results, rec) => {
	results.forEach(result => {
		(result.address_components || []).forEach(component => {
			const { types, long_name, short_name } = component;
			if (types.includes("postal_code") && !rec.PostalCode) {
				rec.PostalCode = long_name;
			}
			if ((types.includes("postal_town") || types.includes("locality")) && !rec.City) {
				rec.City = long_name;
			}
			if (types.includes("administrative_area_level_1") && !rec.State) {
				rec.State = long_name;
				rec.StateCode = short_name;
			}
			if (types.includes("country") && !rec.Country) {
				rec.Country = long_name;
				rec.CountryCode = short_name;
			}
		});
	});
};

const geolocationByAddress = async filter => {
	const rec = { Latitude: 0, Longitude: 0 };
	const address = addressKeys.reduce((accum, key) => filter[key] ? `${accum} ${filter[key]}` : accum, "");

	if (!address.length) {
		return { success: true, recs: [rec], count: 1 };
	}

	const { success, results } = await geocode({ address });
	const [result] = results;

	if (result) {
		const location = result.geometry && result.geometry.location;
		if (location) {
			rec.Latitude = location.lat();
			rec.Longitude = location.lng();
		}
		// the later results are other addresses that matched, only the best one describes this address
		addComponents([result], rec);
	}

	return { success, recs: [rec], count: 1 };
};

const addressByGeolocation = async filter => {
	const rec = { ...filter };
	const { success, results } = await geocode({
		location: {
			lat: Number(filter.Latitude),
			lng: Number(filter.Longitude)
		}
	});

	addComponents(results, rec);

	return { success, recs: [rec], count: 1 };
};

export {
	loadGoogleMaps,
	geolocationByAddress,
	addressByGeolocation
};
